import React from 'react';
import classnames from 'classnames';
import {
  parseMetadata,
  PropsWithMetadata,
  AProps,
  DivProps,
} from '@highlight-ui/utils-commons';
import { Icon } from '@highlight-ui/icon';
import {
  Heading,
  HeadingVariant,
  TokenisedTypographyProps,
  Typography,
} from '@highlight-ui/typography';
import getInitials from './utils';

import styles from './Avatar.module.scss';

export type AvatarRef = React.Ref<HTMLAnchorElement | HTMLDivElement>;

export type AvatarProps = Omit<
  React.ComponentPropsWithoutRef<'a'>,
  'className' | 'href' | 'children'
> &
  PropsWithMetadata<{
    /** If true, then Avatar will be animated on mouse hover */
    animate?: boolean;
    /** A CSS class applied on component container */
    containerClassname?: string;
    /** A CSS class to be applied on the image element */
    imageClassname?: string;
    /** Name of the Avatar. If the image is not available, use first letters of the name  */
    name?: string;
    /** The `width` and `height` attributes for the container element */
    avatarSize?: number;
    /** The `size` attribute for the `Icon` element */
    iconSize?: number;
    /**
     * @deprecated This prop has been replaced by `headingVariant` and will be removed in an upcoming release.
     * The `fontSize` attribute for the `Typography` element
     */
    typographyFontSize?: TokenisedTypographyProps['fontSize'];
    /** The `variant` attribute for the `Heading` element */
    headingVariant?: HeadingVariant;
    /** Image source for the Avatar */
    src?: string;
    /** Url to go to on click */
    url?: string;
    /** Alternative text for an image element */
    imageAltText?: string;
    /** Optional property to indicate how the browser should load images on img tags */
    imageLoading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
    /** A function can be passed as a child node. This function will receive an image of the Avatar.
     * Thus, you can render that image and any additional visuals, like status.
     */
    children?: (fn: () => JSX.Element) => JSX.Element;
  }>;

const getFontSize = (sizes: number): TokenisedTypographyProps['fontSize'] => {
  if (sizes < 16) return 'xsmall';
  if (sizes < 18) return 'small';
  if (sizes < 20) return 'base';
  if (sizes < 22) return 'large';
  if (sizes < 28) return 'xlarge';
  if (sizes < 36) return '2xl';
  return '3xl';
};

const getHeadingFontSize = (sizes: number): HeadingVariant => {
  if (sizes < 16) return 'xsmall';
  if (sizes < 18) return 'small';
  if (sizes < 20) return 'medium';
  if (sizes < 22) return 'large';
  if (sizes < 28) return 'xlarge';
  return '2xlarge';
};

function getAvatarChild(
  src: string,
  name: string,
  imageClassname: string,
  iconSize: number,
  typographyFontSize?: TokenisedTypographyProps['fontSize'],
  headingVariant?: HeadingVariant,
  imageAltText?: string,
  imageLoading?: AvatarProps['imageLoading'],
): JSX.Element {
  const initials = getInitials(name);
  if (src)
    return (
      <img
        className={classnames(styles.avatarImage, imageClassname)}
        src={src}
        alt={imageAltText ?? initials}
        loading={imageLoading}
      />
    );

  if (!name.length) {
    return (
      <Icon name="user" type="solid" size={iconSize} color="icon-default" />
    );
  }

  if (headingVariant) {
    return (
      <Heading
        as="span"
        variant={headingVariant || getHeadingFontSize(iconSize)}
        color="text-subdued"
      >
        {initials}
      </Heading>
    );
  }

  return (
    <Typography
      token="heading-large"
      component="span"
      className={styles.avatarInitials}
      fontSize={typographyFontSize || getFontSize(iconSize)}
    >
      {initials}
    </Typography>
  );
}

function ContainerWithRef(
  { children, ...props }: AProps | DivProps,
  ref: AvatarRef,
) {
  return (props as AProps).href ? (
    <a {...(props as AProps)} ref={ref as React.Ref<HTMLAnchorElement>}>
      {children}
    </a>
  ) : (
    <div {...(props as DivProps)} ref={ref as React.Ref<HTMLDivElement>}>
      {children}
    </div>
  );
}
const Container = React.forwardRef<
  HTMLAnchorElement | HTMLDivElement,
  AProps | DivProps
>(ContainerWithRef);

function AvatarWithRef(props: AvatarProps, ref: AvatarRef) {
  const {
    src = '',
    name = '',
    avatarSize = 46,
    iconSize,
    typographyFontSize,
    headingVariant,
    url,
    containerClassname,
    animate = false,
    imageClassname = '',
    children,
    metadata,
    imageAltText,
    imageLoading,
    ...restProps
  } = props;
  const calculatedIconSize =
    iconSize || (avatarSize === 46 ? 26 : 26 + (avatarSize - 46) / 2);
  return (
    <Container
      {...restProps}
      {...parseMetadata(metadata)}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      href={url}
      style={{ '--avatar-size': `${avatarSize}px` } as React.CSSProperties}
      className={classnames(
        styles.avatarContainer,
        {
          [styles.avatarAnimated]: animate,
        },
        containerClassname,
      )}
      ref={ref}
    >
      {children && typeof children === 'function'
        ? children(() =>
            getAvatarChild(
              src,
              name,
              imageClassname,
              calculatedIconSize,
              typographyFontSize,
              headingVariant,
              imageAltText,
              imageLoading,
            ),
          )
        : getAvatarChild(
            src,
            name,
            imageClassname,
            calculatedIconSize,
            typographyFontSize,
            headingVariant,
            imageAltText,
            imageLoading,
          )}
    </Container>
  );
}

AvatarWithRef.displayName = 'Avatar';

const Avatar = React.forwardRef<
  HTMLAnchorElement & HTMLDivElement,
  AvatarProps
>(AvatarWithRef);
export default Avatar;
